if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        // Enforce required fields (e.g. Ring Size) associated via form="" attribute
        if (!this.validateRequiredFields()) return;

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        try {
          const formData = new FormData(this.form);
          const mainVariantId = Number(formData.get('id'));
          const quantity = Number(formData.get('quantity') || 1);
          const upsellItems =
            window.ProductUpsells && typeof window.ProductUpsells.getSelectedUpsells === 'function'
              ? window.ProductUpsells.getSelectedUpsells(this.form)
              : [];
          const upsellIds = upsellItems.map((item) => item.variantId);

          const properties = {};
          for (const [key, value] of formData.entries()) {
            const match = key.match(/^properties\[(.+)\]$/);
            if (match) properties[match[1]] = value;
          }

          let diamondAttachment = null;
          if (
            window.ProductDiamondOptions &&
            typeof window.ProductDiamondOptions.prepareSelectedDiamondForCart === 'function'
          ) {
            try {
              diamondAttachment = await window.ProductDiamondOptions.prepareSelectedDiamondForCart();
            } catch (diamondError) {
              this.handleErrorMessage(
                (diamondError && diamondError.message) || 'Please select a diamond before adding to cart.'
              );
              return;
            }
          }

          // Diamond is included in ring price — store selection on the ring line only.
          if (diamondAttachment && diamondAttachment.properties) {
            Object.assign(properties, diamondAttachment.properties);
          }

          // Skip variants already in cart so nothing is added twice
          const cartVariantIds =
            window.ProductUpsells && typeof window.ProductUpsells.fetchCartVariantIds === 'function'
              ? await window.ProductUpsells.fetchCartVariantIds()
              : new Set();

          const itemsToAdd = [];

          if (mainVariantId && !cartVariantIds.has(mainVariantId)) {
            itemsToAdd.push({
              id: mainVariantId,
              quantity: quantity,
              ...(Object.keys(properties).length ? { properties } : {}),
            });
          }

          upsellItems.forEach((upsell) => {
            const variantId = upsell && upsell.variantId;
            if (!variantId || cartVariantIds.has(variantId)) return;

            const item = { id: variantId, quantity: 1 };
            if (upsell.isMatchingBand) {
              const pct = Number(upsell.discountPercent) || Number(window.MatchingBandDiscountConfig?.percent) || 10;
              // Underscore props stay internal (hidden in cart/checkout)
              item.properties = {
                _upsell_source: 'matching_band',
                _upsell_discount_percent: String(pct),
              };
            }
            itemsToAdd.push(item);
          });

          // Everything already in cart — just open/refresh the cart
          if (itemsToAdd.length === 0) {
            if (this.cart) {
              await this.refreshCartDrawer();
            } else {
              window.location = window.routes.cart_url;
            }
            return;
          }

          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';

          let requestBody;

          if (itemsToAdd.length > 1 || upsellIds.length > 0 || diamondAttachment) {
            // Multi-item (or upsell/diamond-aware) add via JSON
            const payload = { items: itemsToAdd };
            if (this.cart) {
              payload.sections = this.cart.getSectionsToRender().map((section) => section.id);
              payload.sections_url = window.location.pathname;
              this.cart.setActiveElement(document.activeElement);
            }
            config.headers['Content-Type'] = 'application/json';
            requestBody = JSON.stringify(payload);
          } else {
            // Single main product — keep Dawn FormData flow
            delete config.headers['Content-Type'];
            if (this.cart) {
              formData.append(
                'sections',
                this.cart.getSectionsToRender().map((section) => section.id)
              );
              formData.append('sections_url', window.location.pathname);
              this.cart.setActiveElement(document.activeElement);
            }
            requestBody = formData;
          }

          config.body = requestBody;

          const response = await fetch(`${routes.cart_add_url}`, config).then((res) => res.json());

          if (response.status) {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: mainVariantId,
              errors: response.errors || response.description,
              message: response.message,
            });
            this.handleErrorMessage(response.description);

            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            if (soldOutMessage) {
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
            }
            return;
          }

          if (!this.cart) {
            // Still try to apply discount code in background before redirect
            if (upsellItems.some((item) => item && item.isMatchingBand) && window.MatchingBandDiscount) {
              try {
                await Promise.race([
                  window.MatchingBandDiscount.sync({ force: true, silent: true }),
                  new Promise((resolve) => setTimeout(resolve, 1500)),
                ]);
              } catch (e) {}
            }
            window.location = window.routes.cart_url;
            return;
          }

          // Ensure sections exist so cart drawer can paint (never block on discount)
          if (!response.sections) {
            try {
              const sectionIds = this.cart.getSectionsToRender().map((section) => section.id);
              response.sections = await fetch(`${window.routes.cart_url}?sections=${sectionIds.join(',')}`).then((res) =>
                res.json()
              );
            } catch (e) {}
          }

          const startMarker =
            window.CartPerformance && typeof CartPerformance.createStartingMarker === 'function'
              ? CartPerformance.createStartingMarker('add:wait-for-subscribers')
              : null;
          if (!this.error) {
            const published = publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: mainVariantId,
              cartData: response,
            });
            if (published && typeof published.then === 'function' && startMarker) {
              published.then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            }
          }
          this.error = false;

          const paintCart = () => {
            try {
              if (window.CartPerformance && typeof CartPerformance.measure === 'function') {
                CartPerformance.measure('add:paint-updated-sections', () => {
                  this.cart.renderContents(response);
                });
              } else {
                this.cart.renderContents(response);
              }
            } catch (paintError) {
              console.error(paintError);
              this.refreshCartDrawer().catch(() => {});
            }
          };

          const quickAddModal = this.closest('quick-add-modal');
          if (quickAddModal) {
            document.body.addEventListener(
              'modalClosed',
              () => {
                setTimeout(paintCart);
              },
              { once: true }
            );
            quickAddModal.hide(true);
          } else {
            paintCart();
          }

          // Apply matching-band discount AFTER cart UI opens (non-blocking)
          const hasMatchingBandUpsell = upsellItems.some((item) => item && item.isMatchingBand);
          if (hasMatchingBandUpsell && window.MatchingBandDiscount && typeof window.MatchingBandDiscount.sync === 'function') {
            setTimeout(() => {
              window.MatchingBandDiscount.sync({ force: true })
                .then(() => {
                  if (typeof window.MatchingBandDiscount.refreshCartUI === 'function') {
                    return window.MatchingBandDiscount.refreshCartUI();
                  }
                })
                .catch(() => {});
            }, 50);
          }

          // Keep upsell selection as-is (customer must unselect manually)
        } catch (e) {
          console.error(e);
        } finally {
          this.submitButton.classList.remove('loading');
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          const spinner = this.querySelector('.loading__spinner');
          if (spinner) spinner.classList.add('hidden');

          if (window.CartPerformance && typeof CartPerformance.measureFromEvent === 'function') {
            CartPerformance.measureFromEvent('add:user-action', evt);
          }
        }
      }

      async refreshCartDrawer() {
        if (!this.cart) return;

        const sections = this.cart.getSectionsToRender().map((section) => section.id);
        const response = await fetch(`${window.routes.cart_url}?sections=${sections.join(',')}`).then((res) =>
          res.json()
        );

        // Shape response like cart/add so renderContents works
        const cartPayload = { sections: response };
        this.cart.setActiveElement(document.activeElement);
        CartPerformance.measure('add:paint-updated-sections', () => {
          this.cart.renderContents(cartPayload);
        });
      }

      validateRequiredFields() {
        const isFilled = (field) => {
          if (!field) return true;
          if (field.type === 'radio' || field.type === 'checkbox') return field.checked;
          return !!(field.value && String(field.value).trim());
        };

        const isVisibleField = (field) => {
          if (!field || field.disabled) return false;
          if (field.getAttribute('aria-hidden') === 'true') return false;
          const style = window.getComputedStyle(field);
          if (style.display === 'none' || style.visibility === 'hidden') return false;
          const rect = field.getBoundingClientRect();
          return rect.width > 0 || rect.height > 0;
        };

        const focusInvalid = (field) => {
          if (typeof window.focusRequiredProductField === 'function') {
            window.focusRequiredProductField(field, {
              message: `Please select a ${this.getFieldLabel(field)}.`,
            });
          } else if (field && typeof field.focus === 'function') {
            field.focus();
          }
        };

        const formId = this.form.id;

        // Prefer ring size / band width (and other form-associated required selects)
        // so we never land on a hidden/off-screen control first.
        const requiredSelects = Array.from(
          document.querySelectorAll(
            `select[required][form="${formId}"], select.product-ring-size__select[required]`
          )
        )
          .filter((select) => {
            if (select.form !== this.form && select.getAttribute('form') !== formId) return false;
            return isVisibleField(select);
          })
          .sort((a, b) => {
            const rank = (el) => {
              const name = (el.name || '').toLowerCase();
              if (name.includes('ring size')) return 0;
              if (name.includes('band width')) return 1;
              return 2;
            };
            return rank(a) - rank(b);
          });

        const emptyRequiredSelect = requiredSelects.find((select) => !isFilled(select));
        if (emptyRequiredSelect) {
          focusInvalid(emptyRequiredSelect);
          return false;
        }

        // Other invalid controls associated with this form
        if (typeof this.form.checkValidity === 'function' && !this.form.checkValidity()) {
          const associatedInvalid = Array.from(
            document.querySelectorAll(`[form="${formId}"]:invalid`)
          ).find(isVisibleField);
          const nestedInvalid = Array.from(this.form.querySelectorAll(':invalid')).find(isVisibleField);
          const invalid = associatedInvalid || nestedInvalid;
          if (invalid) {
            focusInvalid(invalid);
            return false;
          }
        }

        return true;
      }

      getFieldLabel(field) {
        const label = field.id ? document.querySelector(`label[for="${field.id}"]`) : null;
        if (label && label.textContent.trim()) return label.textContent.trim().toLowerCase();

        const propertyName = (field.name || '').match(/^properties\[(.+)\]$/);
        return propertyName ? propertyName[1].toLowerCase() : 'value';
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
